import { useParams } from 'react-router';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '../components/ui/Breadcrumb';
import usePoolById from '../hooks/queries/usePoolById';
import PoolInfo from '../components/ProxyPoolDetails/PoolInfo';
import Flex from '../components/ui/Flex';
import ProxyUsageChart from '../components/Dashboard/ProxyUsageChart';
import Proxies from '../components/ProxyPoolDetails/Proxies';
import { LucideEdit3 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/Tooltip';
import { useRef, useState } from 'react';
import { httpApi } from '@fd/common/api/basicHttpApi';
import Page from '../components/ui/Page';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/Tabs';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { DesktopView, MobileView } from '../components/responsive';

async function fetchChangePoolName(poolId: number, name: string) {
  await httpApi(`/pools/edit`, { pool_id: poolId, pool_name: name }, 'POST');
}

export default function ProxyPoolDetailsPage() {
  const params = useParams();
  const poolId = Number(params['proxyPoolId']);
  const { data: pool } = usePoolById(poolId);

  return (
    <Page.Container>
      <Page.Heading>
        <BreadcrumbHeading currentPage={pool?.pool_name} />
        <h1>
          <PoolName />
        </h1>
      </Page.Heading>
      <Page.Body>
        <DesktopView>
          <Flex.Col className="gap-5">
            <Flex.Row className="gap-5">
              <PoolInfo poolId={poolId} />
              <ProxyUsageChart
                poolId={poolId}
                chartHeight={460}
                chartWidth={840}
                className="w-full max-w-full"
                hideProxyTypeFilter
              />
            </Flex.Row>
            <Proxies poolId={poolId} />
          </Flex.Col>
        </DesktopView>
        <MobileView>
          <Tabs defaultValue="proxies">
            <TabsList className="w-full">
              <TabsTrigger value="proxies" className="w-full">
                Proxies
              </TabsTrigger>
              <TabsTrigger value="usage" className="w-full">
                Usage
              </TabsTrigger>
            </TabsList>
            <TabsContent value="proxies" className="flex flex-col gap-5">
              <PoolInfo poolId={poolId} />
              <Proxies poolId={poolId} />
            </TabsContent>
            <TabsContent value="usage" className="flex flex-col gap-5">
              <ProxyUsageChart poolId={poolId} hideProxyTypeFilter />
            </TabsContent>
          </Tabs>
        </MobileView>
      </Page.Body>
    </Page.Container>
  );
}

function PoolName() {
  const params = useParams();
  const poolId = Number(params['proxyPoolId']);
  const { data: pool, mutate, isValidating } = usePoolById(poolId);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      changePoolName();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
    }
  }

  async function changePoolName() {
    setIsEditing(false);
    setIsSaving(true);
    const newName = inputRef.current?.value;
    if (!newName) return;
    await fetchChangePoolName(poolId, newName);
    mutate();
    setIsSaving(false);
  }

  return (
    <Flex.Row className="gap-2 items-center">
      {isEditing && (
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            onKeyDown={handleInputKeyDown}
            ref={inputRef}
            maxLength={40}
            className="block min-w-0 grow py-1.5 pl-1 pr-3 text-gray-900 placeholder:text-gray-400 focus:outline-0 bg-white"
            defaultValue={pool?.pool_name}
          />
          <div className="flex flex-row gap-2">
            <Button onClick={changePoolName}>Save</Button>
            <Button
              variant="secondary"
              onClick={() => setIsEditing(!isEditing)}
            >
              <span>Dismiss</span>
            </Button>
          </div>
        </div>
      )}
      {!isEditing && (
        <>
          <span>
            {isValidating && isSaving ? 'Updating name...' : pool?.pool_name}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="cursor-text">
                <span className="text-black/30 font-normal"> #{pool?.id}</span>
              </TooltipTrigger>
              <TooltipContent>
                <span>Pool unique identifier</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <LucideEdit3
            className="h-6 w-6 stroke-icon-secondary cursor-pointer"
            onClick={() => setIsEditing(!isEditing)}
          />
        </>
      )}
    </Flex.Row>
  );
}

function BreadcrumbHeading({ currentPage }: { currentPage?: string }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink href="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink>{currentPage}</BreadcrumbLink>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}
